import type { FC } from "react";
import { DataTestIds } from "src/shared/constants/TestEnums";
import { useContent } from "src/shared/components/content";
import { GADayPassSelector } from "./ga-day-pass-selector";
import { useGaFilter } from "./ga-filter-state";
import { GAPassTypeChips } from "./ga-pass-type-chips";
import { GAWeekendSelector } from "./ga-weekend-selector";
import { chipsSpacingStyle } from "./styles";

/**
 * GA filter section — pure rendering component that reads all derived
 * filter data from useGaFilter. No local derivation or grid-item
 * iteration; the context owns the single-pass derivation loop.
 */
export const GAFilterSection: FC = () => {
  const filterCtx = useGaFilter();
  // Accessible labels for selector groups
  const weekendAriaLabel = useContent("cid$#4300594#"); // "Weekend selection"
  const dayPassAriaLabel = useContent("cid$#4300595#"); // "Pass duration selection"

  if (!filterCtx) return null;

  const { hasWeekends, hasDayPasses, availableFilters } = filterCtx;
  const hasSelectors = hasWeekends || hasDayPasses;
  const hasMultiplePassTypes = availableFilters.size > 1;
  const hasAnyFilters = hasMultiplePassTypes || hasSelectors;

  if (!hasAnyFilters) return null;

  return (
    <>
      <div className={hasSelectors ? undefined : chipsSpacingStyle} data-testid={DataTestIds.GaFilterSection}>
        <GAPassTypeChips
          selected={filterCtx.passTypeFilter}
          onSelect={filterCtx.setPassTypeFilter}
          availableFilters={filterCtx.availableFilters}
        />
      </div>
      {/* Mutually exclusive: show weekends if available, otherwise day passes */}
      {hasWeekends ? (
        <GAWeekendSelector
          weekends={filterCtx.weekends}
          selectedKey={filterCtx.effectiveWeekend}
          onSelect={filterCtx.setSelectedWeekend}
          ariaLabel={weekendAriaLabel}
        />
      ) : hasDayPasses ? (
        <GADayPassSelector
          options={filterCtx.dayPassOptions}
          selectedPassLength={filterCtx.effectivePassLength}
          onSelect={filterCtx.setSelectedPassLength}
          ariaLabel={dayPassAriaLabel}
        />
      ) : null}
    </>
  );
};
